import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LoginResult, SessionUser } from '@mealdesk/shared';

/**
 * The refresh token is deliberately absent. It lives in an httpOnly cookie the
 * browser attaches by itself, so nothing on this page can read it — which
 * matters when the session lasts ninety days (§4.6).
 *
 * localStorage rather than sessionStorage, because this is a phone in a pocket:
 * closing the tab must not mean signing in again.
 */
type Session = Omit<LoginResult, 'refreshToken'>;

type AuthState = {
  user: SessionUser | null;
  accessToken: string | null;
  expiresAt: string | null;
  setSession: (session: Session) => void;
  clear: () => void;
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      expiresAt: null,
      setSession: (session) =>
        set({
          user: session.user,
          accessToken: session.accessToken,
          expiresAt: session.expiresAt,
        }),
      clear: () => set({ user: null, accessToken: null, expiresAt: null }),
    }),
    { name: 'mealdesk.web.auth' },
  ),
);

/** Under four minutes left: time to rotate before a request needs it. */
export function isTokenExpiringSoon(expiresAt: string | null): boolean {
  if (!expiresAt) return true;
  return new Date(expiresAt).getTime() - Date.now() < 4 * 60_000;
}
